import { clamp, type Placement } from '@eip/engine';
import { OFFSET_IN_MAX, WIDTH_IN_MAX, WIDTH_IN_MIN } from '../state/mockup';

/**
 * One-click placements and nine-way alignment, in the area's own inch space.
 *
 * PURE, and the whole of the arithmetic behind the panel's Preset select and Align grid: the panel
 * calls these and writes the result through the same job write its Width and offset fields use, so
 * a preset is undone by one `Ctrl+Z` labelled "Placement" and re-renders exactly like a typed inch.
 *
 * Two rules hold everywhere below. A width is clamped into `[WIDTH_IN_MIN, area.widthIn]` — the
 * area's own width is the ceiling, not the panel's 40 in, because the compositor would only clip
 * what runs past it. And an x or y is never negative: a preset that would start off the left edge
 * or above the top starts at 0 instead. Both ends of every offset are held to the panel's own
 * `[0, OFFSET_IN_MAX]` as well, so what these return is exactly what the job write stores — which
 * is what lets `matchPreset` recognise its own output afterwards.
 *
 * A centred axis is written as `'center'`, not as the number it works out to: that is the sticky
 * centre `artRectIn` re-resolves at every width, and it is what the panel's "Centre across"/"Centre
 * down" toggles read. So the toggles follow the Align grid's middle column and row for free.
 */

export type PresetId = 'left-chest' | 'right-chest' | 'centre-chest' | 'full-front' | 'top-centre' | 'bottom-centre';

export interface PlacementPreset { id: PresetId; label: string; hint: string }

/** An area to place in — a `PrintArea`'s two inch measurements, which is all any of this reads. */
interface Area { widthIn: number; heightIn: number }
/** The art's pixel size, for the one thing width alone cannot give: how TALL the print is. */
interface Art { width: number; height: number }

/**
 * The presets, in the order the select offers them: the two chest prints a shop asks for by name
 * first, then the three centred ones from biggest to smallest, then the bottom.
 *
 * "Left chest" is the WEARER's left, which is the RIGHT half of a front view as you look at it —
 * the single thing about garment placement that is got wrong most often, so both the label and the
 * hint say it out loud rather than leaving the shop to find out at the press.
 */
export const PLACEMENT_PRESETS: readonly PlacementPreset[] = [
  { id: 'left-chest', label: "Left chest (wearer's left)", hint: "Pocket-size print on the wearer's left — the right side as you look at it" },
  { id: 'right-chest', label: "Right chest (wearer's right)", hint: "Pocket-size print on the wearer's right — the left side as you look at it" },
  { id: 'centre-chest', label: 'Centre chest', hint: 'The usual chest print: centred across, a little down from the top' },
  { id: 'full-front', label: 'Full front', hint: 'As wide as the area, starting near the top' },
  { id: 'top-centre', label: 'Top centre (small)', hint: 'A small print high up, centred across' },
  { id: 'bottom-centre', label: 'Bottom centre', hint: 'Centred across, half an inch off the bottom of the area' },
];

/** How far a chest print's near edge sits from the middle of the area. */
const CHEST_GAP_IN = 1.25;
/** How far down a chest print starts, and how far up a bottom-centre one stops. */
const CHEST_TOP_IN = 1, BOTTOM_GAP_IN = 0.5;
/** Matching tolerance: half the panel's own 0.1-in field step, so a value the shop typed reads
 *  back as the preset it came from while one deliberate nudge does not. */
const MATCH_TOL_IN = 0.05;

/**
 * Hundredths of an inch, always DOWNWARD. The fields step in tenths and `4.2915000000000005` is not
 * a position, so everything here is rounded — but a width or an offset worked out from an edge must
 * never round OUTWARD, or aligning a print hard against the bottom of its area would trip the
 * panel's own over-size warning by five thousandths of an inch. Every value the presets name
 * (1, 1.5, 0.5, 7.25) is exact in hundredths, so this only ever moves a derived one, inwards, by
 * less than a hundredth.
 */
const floor2 = (v: number) => Math.floor(v * 100) / 100;
/** The art's own height at a placed width — `artRectIn`'s arithmetic. 0 for unmeasurable art. */
const artHeightIn = (widthIn: number, art: Art) => (art.width > 0 ? (widthIn * art.height) / art.width : 0);
/** A preset's wanted width, held to the area (and to the panel's own range). */
const presetWidth = (wantIn: number, area: Area) => clamp(floor2(Math.min(wantIn, area.widthIn)), WIDTH_IN_MIN, WIDTH_IN_MAX);
/** An offset, never negative and never past what the panel's own fields accept. */
const offsetIn = (v: number) => clamp(floor2(v), 0, OFFSET_IN_MAX);

/**
 * How an alignment writes a CENTRED axis.
 *
 * `sticky` (the default) writes the `'center'` the engine re-resolves at every width — right for the
 * primary print, whose panel row has a "Centre across"/"Centre down" toggle to show it and a field
 * that displays the inch it works out to while the toggle owns the axis.
 *
 * A "More prints" row has neither: its X and Y are plain sliders, which read a `'center'` as 0 and
 * would throw a centred print at the edge on the next nudge (`defaultExtraPlacement` writes numbers
 * for exactly this reason). Those rows pass `sticky: false` and get the NUMBER instead — the same
 * position today, just not one that re-centres itself when the width changes.
 */
export interface AlignOptions { sticky?: boolean }

/** How close a number has to be to the centre to BE the centre, for `currentAnchor` on a row that
 *  writes numeric centres: half the hundredth everything here is rounded to. */
const CENTRE_TOL_IN = 0.005;

/** Where a `'center'` axis actually lands, so two placements can be compared edge to edge. */
const resolved = (v: number | 'center', extentIn: number, sizeIn: number) => (v === 'center' ? (extentIn - sizeIn) / 2 : v);

/** The placement one preset asks for in `area`, for art of this shape. */
export function applyPreset(id: PresetId, area: Area, art: Art): Placement {
  switch (id) {
    case 'left-chest':
    case 'right-chest': {
      const widthIn = presetWidth(4, area);
      // The wearer's left is the viewer's right: the near edge sits 1.25 in past the middle for
      // "left chest", and 1.25 in short of it (minus its own width) for "right chest". Either is
      // then pulled back inside the area rather than hanging off the right edge.
      const xIn = id === 'left-chest' ? area.widthIn / 2 + CHEST_GAP_IN : area.widthIn / 2 - CHEST_GAP_IN - widthIn;
      return { widthIn, xIn: offsetIn(Math.min(xIn, area.widthIn - widthIn)), yIn: offsetIn(CHEST_TOP_IN) };
    }
    case 'centre-chest':
      return { widthIn: presetWidth(10, area), xIn: 'center', yIn: offsetIn(1.5) };
    case 'full-front':
      return { widthIn: presetWidth(area.widthIn, area), xIn: 'center', yIn: offsetIn(0.5) };
    case 'top-centre':
      return { widthIn: presetWidth(6, area), xIn: 'center', yIn: offsetIn(0.5) };
    case 'bottom-centre': {
      const widthIn = presetWidth(8, area);
      // Measured on the ART's height, not the print's width: a tall design placed 8 in wide is
      // most of the area, and a y guessed from the width would put it through the hem.
      return { widthIn, xIn: 'center', yIn: offsetIn(area.heightIn - artHeightIn(widthIn, art) - BOTTOM_GAP_IN) };
    }
  }
}

/**
 * Which preset a placement IS — the select's displayed value — or `'custom'` for one that is none
 * of them. Compared to within a twentieth of an inch on the width and on each resolved edge, so a
 * shop that typed the numbers by hand still sees the preset it matched, while one deliberate 0.1-in
 * nudge reads as "Custom".
 *
 * `'center'` is asymmetric on purpose: a placement that carries the sticky centre matches only a
 * preset whose own axis is centred, while a NUMBER matches by where it lands — the Centre toggles
 * hand an axis over at the very inch it was centred at, and that is the same placement to the eye
 * and to the render.
 *
 * The FIRST match wins. Presets collide only on an area small enough to clamp two of them to the
 * same width ("Full front" and "Top centre" are one placement in a 5-in area), and answering with
 * the earlier of the two is stable rather than right — nothing downstream can tell them apart.
 */
export function matchPreset(area: Area, art: Art, p: Placement): PresetId | 'custom' {
  for (const preset of PLACEMENT_PRESETS) {
    const q = applyPreset(preset.id, area, art);
    if (Math.abs(p.widthIn - q.widthIn) > MATCH_TOL_IN) continue;
    const qH = artHeightIn(q.widthIn, art);
    const xSame = p.xIn === 'center'
      ? q.xIn === 'center'
      : Math.abs(p.xIn - resolved(q.xIn, area.widthIn, q.widthIn)) <= MATCH_TOL_IN;
    const ySame = p.yIn === 'center'
      ? q.yIn === 'center'
      : Math.abs(p.yIn - resolved(q.yIn, area.heightIn, qH)) <= MATCH_TOL_IN;
    if (xSame && ySame) return preset.id;
  }
  return 'custom';
}

/** The nine cells of the Align grid, in reading order — the order the grid mounts them in. */
export type Anchor = 'tl' | 't' | 'tr' | 'l' | 'c' | 'r' | 'bl' | 'b' | 'br';
export const ANCHORS: readonly Anchor[] = ['tl', 't', 'tr', 'l', 'c', 'r', 'bl', 'b', 'br'];

/** What each button says it does, which is also its accessible name. */
export const ANCHOR_LABELS: Record<Anchor, string> = {
  tl: 'Align top left', t: 'Align top', tr: 'Align top right',
  l: 'Align left', c: 'Align centre', r: 'Align right',
  bl: 'Align bottom left', b: 'Align bottom', br: 'Align bottom right',
};

const COLUMN: Record<Anchor, 'l' | 'c' | 'r'> = { tl: 'l', t: 'c', tr: 'r', l: 'l', c: 'c', r: 'r', bl: 'l', b: 'c', br: 'r' };
const ROW: Record<Anchor, 't' | 'c' | 'b'> = { tl: 't', t: 't', tr: 't', l: 'c', c: 'c', r: 'c', bl: 'b', b: 'b', br: 'b' };

/**
 * The placement `p` moved to an edge, a middle or a corner of `area`. The WIDTH is never touched —
 * aligning is a move, and a shop that wanted the print resized has the Width control for it.
 *
 * The middle column and the middle row write the sticky `'center'` by default, which is why
 * pressing the grid's middle button ticks the panel's "Centre across" and "Centre down" rather than
 * leaving two numbers that only happen to be centred at today's width. A caller with no such toggle
 * to show for it passes `sticky: false` and gets the number (see `AlignOptions`).
 *
 * Rotation is not read: an aligned print that is also turned can still run past the edge it was
 * aligned to (the rotated bounding box is bigger than the rect), and the panel's over-size warning
 * reports that as it reports any other overhang.
 */
export function alignPlacement(anchor: Anchor, area: Area, art: Art, p: Placement, opts: AlignOptions = {}): Placement {
  const widthIn = p.widthIn, heightIn = artHeightIn(widthIn, art);
  const column = COLUMN[anchor], row = ROW[anchor];
  const centre = (extentIn: number, sizeIn: number): number | 'center' =>
    (opts.sticky === false ? offsetIn((extentIn - sizeIn) / 2) : 'center');
  return {
    widthIn,
    xIn: column === 'c' ? centre(area.widthIn, widthIn) : column === 'l' ? 0 : offsetIn(area.widthIn - widthIn),
    yIn: row === 'c' ? centre(area.heightIn, heightIn) : row === 't' ? 0 : offsetIn(area.heightIn - heightIn),
  };
}

/**
 * Which grid button is the placement's own, or `null` for one between them — the grid's pressed
 * state. EXACT matches only, unlike `matchPreset`: the grid is a set of nine positions rather than
 * a description of where the art roughly is, and a button that lights up for a placement a press
 * of it would still move is worse than none lit at all.
 *
 * `opts` must be the ones the grid ALIGNS with, or the pressed cell and the button that wrote it
 * would disagree. The one loosening is the centre of a `sticky: false` caller: a numeric centre is
 * the centre within half a hundredth of an inch (what the rounding here, and the tenths the rest of
 * the app writes, can put between two ways of saying the same position), and a stored `'center'`
 * counts as the centre for such a caller too — it is the same place, written the other way.
 *
 * The first anchor wins where two describe one placement (a print exactly filling its area is at
 * both edges of both axes, and reads as "top left").
 */
export function currentAnchor(area: Area, art: Art, p: Placement, opts: AlignOptions = {}): Anchor | null {
  const axisSame = (q: number | 'center', v: number | 'center', centred: boolean) => {
    if (v === 'center') return q === 'center' || centred;
    if (typeof q !== 'number') return false;
    return Math.abs(q - v) <= (centred ? CENTRE_TOL_IN : 1e-9);
  };
  for (const anchor of ANCHORS) {
    const q = alignPlacement(anchor, area, art, p, opts);
    if (axisSame(q.xIn, p.xIn, COLUMN[anchor] === 'c') && axisSame(q.yIn, p.yIn, ROW[anchor] === 'c')) return anchor;
  }
  return null;
}
